import {
  BillingInterval,
  PaymentStatus,
  SubscriptionTier,
  type CancelSubscription,
  type CancelSubscriptionDto,
  type CreateCheckoutSubscription,
  type CreateCheckoutSubscriptionDto,
  type Price,
  type PriceDto,
  type Subscription,
  type SubscriptionDto,
  type SubscriptionsPage,
  type SubscriptionsPageDto,
  type UpdateSubscription,
  type UpdateSubscriptionDto,
} from './subscriptions.types';

/**
 * `tier`, `interval` and `payment_status` are untyped strings on the response even
 * though the request bodies constrain them — read defensively instead of casting.
 */
const parseEnum = <T extends Record<string, string>>(values: T, raw: string): T[keyof T] | null =>
  (Object.values(values) as string[]).includes(raw) ? (raw as T[keyof T]) : null;

// Go zero-values empty timestamps to '', which is not a date.
const parseDate = (raw: string): string | null => raw || null;

export const mapSubscription = (dto: SubscriptionDto): Subscription => ({
  id: dto.id,
  serviceId: dto.service_id,
  tier: parseEnum(SubscriptionTier, dto.tier),
  interval: parseEnum(BillingInterval, dto.interval),
  seats: dto.seats ?? 0,
  crewSeats: dto.crew_seats ?? 0,
  paymentStatus: parseEnum(PaymentStatus, dto.payment_status),
  paymentStatusRaw: dto.payment_status,
  nextPaymentAt: parseDate(dto.next_payment_at),
  cancelAtPeriodEnd: dto.cancel_at_period_end,
  cancelAt: parseDate(dto.cancel_at),
  canceledAt: parseDate(dto.canceled_at),
  captainWillAddOwner: dto.captain_will_add_owner ?? false,
  manuallyGranted: dto.manually_granted ?? false,
});

export const mapSubscriptionsPage = (dto: SubscriptionsPageDto): SubscriptionsPage => ({
  count: dto.count,
  items: (dto.items ?? []).map(mapSubscription),
});

export const mapPrice = (dto: PriceDto): Price => ({
  id: dto.id,
  lookupKey: dto.lookup_key,
  amount: +dto.amount.number,
  currency: dto.amount.currency,
});

export const mapCreateCheckoutToDto = (
  input: CreateCheckoutSubscription,
): CreateCheckoutSubscriptionDto => ({
  tier: input.tier,
  interval: input.interval,
  seats: input.seats,
  crew_seats: input.crewSeats,
  ...(input.willAddOwner !== undefined && { will_add_owner: input.willAddOwner }),
});

/**
 * Omitted keys keep their current server-side value, so undefined fields are dropped
 * rather than sent as `null`.
 */
export const mapUpdateSubscriptionToDto = (input: UpdateSubscription): UpdateSubscriptionDto => ({
  ...(input.tier !== undefined && { tier: input.tier }),
  ...(input.interval !== undefined && { interval: input.interval }),
  ...(input.seats !== undefined && { seats: input.seats }),
  ...(input.crewSeats !== undefined && { crew_seats: input.crewSeats }),
  // The one field whose omission is not "leave it alone" — see the DTO's note. Callers that
  // can answer the question send the answer they are holding, changed or not.
  ...(input.willAddOwner !== undefined && { will_add_owner: input.willAddOwner }),
});

export const mapCancelSubscriptionToDto = (input: CancelSubscription): CancelSubscriptionDto => ({
  ...(input.feedback !== undefined && { feedback: input.feedback }),
  ...(input.comment && { comment: input.comment }),
});
